// utils/applicationDataMapper.js
// Преобразование данных из API в формат для компонентов

// Маппинг статусов
const STATUS_MAP = {
  'need-check': { text: 'Заявку прийнято', class: 'status-pending' },
  'approved': { text: 'Підтверджена - садочки обрані', class: 'status-verified' },
  'invite-sent': { text: 'Надіслане запрошення', class: 'status-warning' },
  'invite-accepted': { text: 'Запрошення прийняте', class: 'status-success' },
  'declined': { text: 'Заява відхилена', class: 'status-error' },
  'child-in-kindergarten': { text: 'Дитина оформлена', class: 'status-success' },
  'banned': { text: 'Не обслуговується', class: 'status-error' },
  'removed': { text: 'Видалена', class: 'status-error' }
};

// Маппинг типов мест
const PLACE_TYPE_MAP = {
  'standart': 'повного дня',
  'without-bed': 'короткотривале',
  'inclusive': 'інклюзивне',
  'inclusive-short': 'інклюзивне короткотривале'
};

const mapApplicationToComponentData = (applicationData) => {
  if (!applicationData) return null;

  const currentStatus = applicationData.currentStatus?.name || 'need-check';
  const statusConfig = STATUS_MAP[currentStatus] || STATUS_MAP['need-check'];

  return {
    id: applicationData._id || applicationData.registrationNumber,
    registrationNumber: applicationData.registrationNumber,
    queueNumber: applicationData.queueNumber,
    status: statusConfig.text,
    statusClass: statusConfig.class,
    currentStatus: currentStatus,
    invite: applicationData.invite,
    
    // Данные ребенка
    childLastName: applicationData.child?.lastName,
    childFirstName: applicationData.child?.firstName,
    childMiddleName: applicationData.child?.secondName,
    birthDate: applicationData.child?.birthDate,
    gender: applicationData.child?.gender,
    birthCertificate: applicationData.child?.birthCertificate?.number,
    certificateIssueDate: applicationData.child?.birthCertificate?.issueDate,
    certificateCountry: applicationData.child?.birthCertificate?.issueCountry,
    registeredAddress: applicationData.child?.address,
    actualAddress: getActualAddress(applicationData.child),
    sameAddress: applicationData.child?.sameFactAddress === 'yes',
    
    // Данные заявителя
    applicantLastName: applicationData.responsiblePerson?.lastName,
    applicantFirstName: applicationData.responsiblePerson?.firstName,
    applicantMiddleName: applicationData.responsiblePerson?.middleName,
    citizenship: applicationData.responsiblePerson?.citizenship,
    hasRnokpp: applicationData.responsiblePerson?.hasRnokpp === 'yes',
    rnokpp: applicationData.responsiblePerson?.code,
    documentType: applicationData.responsiblePerson?.typeOfDoc,
    documentDetails: applicationData.responsiblePerson?.documentDetails,
    phone: applicationData.responsiblePerson?.contacts?.phone,
    email: applicationData.responsiblePerson?.contacts?.email,
    applicantAddress: applicationData.responsiblePerson?.contacts?.address,
    
    // Предпочтения
    desiredDate: applicationData.preferDateFrom,
    ageMenu: applicationData.youngerGroup ? 'younger' : 'regular',
    inclusiveGroup: applicationData.neededPlaceDescription?.inclusive,
    diseaseType: applicationData.specialization?.specializationId,
    diseaseDocument: applicationData.specialization?.medicalProtocol,
    
    // Льготы
    benefits: mapBenefits(applicationData.child?.benefits),
    
    // Выбранные садики
    selectedSchools: mapSelectedSchools(applicationData),
    
    // Статусы регистрации и льгот
    registrationStatus: getRegistrationStatus(applicationData.statusMR),
    benefitStatus: getBenefitStatus(applicationData.child?.benefits),
    
    // Документы
    registrationDocuments: mapDocuments(applicationData.child?.certScan, 'registration'),
    benefitDocuments: mapDocuments(extractBenefitDocuments(applicationData.child?.benefits), 'benefit'),
    
    // История
    history: mapHistory(applicationData.statuses),
    
    // Аудит отклонения
    rejectionAudit: mapRejectionAudit(applicationData.statuses),
    
    agreePersonalData: true
  };
};

const getActualAddress = (childData) => {
  if (childData?.addressDictFact) {
    const address = childData.addressDictFact;
    return [address.street?.name, address.building?.name, address.corpus?.name]
      .filter(Boolean)
      .join(', ');
  }
  return childData?.address || '';
};

const getRegistrationStatus = (statusMR) => {
  return statusMR?.result === 'verified' ? 'verified' : 'not_verified';
};

const getBenefitStatus = (benefits) => {
  if (!benefits || benefits.length === 0) return 'not_verified';
  
  const hasApproved = benefits.some(benefit => 
    benefit.approvedByUser || benefit.isApproved
  );
  
  return hasApproved ? 'verified' : 'not_verified';
};

const mapBenefits = (benefits) => {
  if (!benefits) return [];
  
  return benefits.map(benefit => ({
    id: benefit._id,
    benefitId: benefit.benefitId,
    benefitName: benefit.benefitName,
    documentNumber: benefit.documentNumber,
    documentIssueDate: benefit.documentIssueDate,
    documentType: benefit.documentType,
    documentTo: benefit.documentTo, // Добавлено
    kindergartenId: benefit.kindergartenId, // Добавлено
    address: benefit.address, // Добавлено
    isApproved: benefit.isApproved, // Добавлено
    approved: !!benefit.approvedByUser || benefit.isApproved, // Обновлено
    approvedBy: benefit.approvedByUser?.kyivIdUserEmail,
    documents: mapDocuments(benefit.benScan, 'benefit'),
    // Добавляем дополнительные поля для отображения
    issueDateFormatted: formatDate(benefit.documentIssueDate),
    createdAt: benefit.createdAt,
    updatedAt: benefit.updatedAt
  }));
};

const mapSelectedSchools = (applicationData) => {
  // В реальном приложении здесь будет логика получения выбранных садиков

  return applicationData.criterias[0].values || [];
  //return applicationData.selectedKindergartensDistricts || [];
};

const mapDocuments = (documents, type) => {
  if (!documents) return [];
  
  return documents.map((doc, index) => ({
    id: doc._id || `doc-${type}-${index}`,
    name: doc.file?.name || `Документ ${index + 1}`,
    type: getFileType(doc.file?.mime),
    size: formatFileSize(doc.file?.size),
    uploadedAt: formatDate(doc.file?.datetimeupload),
    status: doc.file?.securuty?.ro ? 'verified' : 'pending',
    fileId: doc.file?.storage?.name,
    originalName: doc.file?.name
  }));
};

const extractBenefitDocuments = (benefits) => {
  if (!benefits) return [];
  
  return benefits.flatMap(benefit => benefit.benScan || []);
};

const mapHistory = (statuses) => {
  if (!statuses) return [];
  
  return statuses.map((status, index) => ({
    id: status._id || `history-${index}`,
    date: status.date,
    action: getStatusText(status.name),
    actionType: status.name, // Добавляем тип действия для фильтрации
    result: getHistoryResult(status.name),
    additionalInfo: getAdditionalInfo(status),
    user: getUserInfo(status.action),
    kindergartenId: status.kindergartenId,
    groupNumber: status.groupNumber,
    placeType: status.placeType, // Добавляем тип места
    dateTo: status.dateTo // Добавляем срок действия
  }));
};

const mapRejectionAudit = (statuses) => {
  if (!statuses) return [];
  
  const rejectionStatuses = statuses.filter(status => 
    ['declined', 'removed', 'banned'].includes(status.name)
  );
  
  return rejectionStatuses.map((status, index) => ({
    id: status._id || `audit-${index}`,
    fullName: getUserFullName(status.action),
    role: getUserRole(status.action),
    email: status.action?.detail?.email,
    timestamp: formatDateTime(status.date),
    status: status.name,
    reason: status.action?.detail?.comment
  }));
};

const getStatusText = (statusName) => {
  const statusConfig = STATUS_MAP[statusName];
  return statusConfig ? statusConfig.text : statusName;
};

const getHistoryResult = (statusName) => {
  const resultMap = {
    'need-check': 'В очікуванні',
    'approved': 'Успішно',
    'invite-sent': 'Інформація',
    'invite-accepted': 'Успішно',
    'declined': 'Відхилено',
    'child-in-kindergarten': 'Завершено',
    'banned': 'Відхилено',
    'removed': 'Відхилено'
  };
  return resultMap[statusName] || 'В очікуванні';
};

const getAdditionalInfo = (status) => {
  const info = [];
  
  if (status.kindergartenId) {
    info.push(`Дитячий садок: ${status.kindergartenId}`);
  }
  
  if (status.groupNumber) {
    info.push(`Група: ${status.groupNumber}`);
  }
  
  if (status.action?.detail?.comment) {
    info.push(`Коментар: ${status.action.detail.comment}`);
  }
  
  return info.join('; ');
};

const getUserInfo = (action) => {
  if (!action) return 'Заявник';
  
  if (action.source === 'system') {
    return 'Система';
  }
  
  if (action.source === 'user' && action.detail) {
    return action.detail.name || 'Користувач';
  }
  
  return 'Заявник';
};

const getUserFullName = (action) => {
  if (!action || !action.detail) return 'Заявник';
  
  return action.detail.name || 'Користувач';
};

const getUserRole = (action) => {
  if (!action) return 'Заявник';
  
  const roleMap = {
    'system': 'Система',
    'user': 'Оператор',
    'applicant': 'Заявник'
  };
  
  return roleMap[action.source] || 'Заявник';
};

const getFileType = (mimeType) => {
  const typeMap = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'application/zip': 'zip',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx'
  };
  return typeMap[mimeType] || 'file';
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Б';
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

 const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('uk-UA');
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('uk-UA');
};

export { mapApplicationToComponentData, formatDateTime }